import { useEffect } from 'react';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Col, Menu, Row } from 'antd';

import { PageProps } from '@/types/PageProps';

import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import CustomRoute from '@/components/_common/CustomRoute';
import CompanyFooter from '@/components/Companies/CompanyFooter';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { isAppPortocar } from '@/index';
import { MatrixPartnerOrigin } from '@/types/Register';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

function CompanySettingsPage({ route }: PageProps) {
  const match = useRouteMatch<{ companyId: string }>();
  const { company, disabled } = useCompanyFormContext();

  const history = useHistory();

  const { companyId } = match.params;

  useEffect(() => {
    if (match.isExact) {
      history.push(`/app/registry/companies/${companyId}/settings/workorder`);
    }
  }, []);

  const historyKeys = history?.location?.pathname?.split('/');
  const settingsKeyIndex = historyKeys.indexOf('settings');
  const selectedMenuKey = historyKeys[settingsKeyIndex + 1];

  const matrix = useMatrix();

  const isBoschPartner =
    !!matrix.data?.partnerOrigin &&
    [
      MatrixPartnerOrigin.BOSCH_CAR_SERVICE,
      MatrixPartnerOrigin.BOSCH_MODULE_SPECIALIST,
    ].includes(matrix.data?.partnerOrigin);
  const isMichelinIntegration = matrix.data?.matrixSettings.michelinIntegration;

  const handleValidateRoutes = () => {
    return route.routes.filter((r) => {
      if (
        (!isMichelinIntegration && r.key === 'integrations') ||
        (!isBoschPartner && r.key === 'boschCoreCredentials') ||
        (isBoschPartner &&
          r.key === 'boschCoreCredentials' &&
          !hasPermissions([Authorities.BOSCH_FEATURES_CONFIG_CORE_CREDENTIALS]))
      ) {
        return false;
      }

      if (
        (!isAppPortocar || !company?.capsCode) &&
        r.key === 'portoServiceSchedule'
      )
        return false;

      return true;
    });
  };

  const filteredRoutes = handleValidateRoutes();

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={route.parent}
      baseKey={companyId}
      basePath={`/app/registry/companies/${companyId}`}
    >
      <Row className="settings-wrapper" gutter={[0, 32]}>
        <Col span={24} lg={6}>
          <Menu mode="inline" className="menu" selectedKeys={[selectedMenuKey]}>
            {filteredRoutes?.map(
              (r) =>
                !r.key.includes('stone') && (
                  <Menu.Item key={r.key} disabled={r.path.includes('stone')}>
                    <Link to={r.path.replace(':companyId', companyId)}>
                      <FormattedMessage id={r.name} />
                    </Link>
                  </Menu.Item>
                ),
            )}
          </Menu>
        </Col>

        <Col span={24} lg={18}>
          {filteredRoutes?.map((r) => (
            <CustomRoute {...r} key={r.key} />
          ))}
        </Col>
      </Row>

      <CompanyFooter disabled={disabled} />
    </DefaultRouteTabs>
  );
}

export default CompanySettingsPage;
